import { useEffect, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { api } from "@/lib/api";
import { Logo } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import {
  ArrowLeft, ArrowRight, CheckCircle, Quotes, Clock, ChatCircleDots,
  MapPin, Buildings, PaperPlaneTilt, Confetti,
  LinkedinLogo, WhatsappLogo, XLogo, ShareNetwork, Copy
} from "@phosphor-icons/react";

const PUBLIC_URL = "https://otelya.app";

function Nav() {
  return (
    <header className="sticky top-0 z-40 backdrop-blur-xl bg-canvas/80 border-b border-line/70">
      <div className="max-w-5xl mx-auto flex items-center justify-between px-6 py-4">
        <Logo />
        <Link to="/" className="text-sm text-stone hover:text-ink flex items-center gap-1" data-testid="case-study-back-home">
          <ArrowLeft size={14}/> Otelya
        </Link>
      </div>
    </header>
  );
}

function daysLabel(d) {
  if (d === null || d === undefined) return "—";
  if (d === 0) return "stesso giorno";
  if (d === 1) return "1 giorno";
  return `${d} giorni`;
}

// ------------------------------- INDEX -------------------------------

export function CaseStudiesIndex() {
  const [items, setItems] = useState(null);
  useEffect(() => {
    api.get("/public/case-studies").then(r => setItems(r.data?.items || [])).catch(() => setItems([]));
  }, []);

  const seoTitle = "Case Study · Clienti che hanno scelto Otelya";
  const seoDesc = "Storie reali di hotel, B&B e agriturismi che hanno affidato la ricezione H24 alla receptionist AI di Otelya. Dati veri: giorni-to-close, scambi AI, testimonianza del gestore.";
  // Dynamic OG collage image generated by backend (3-structure strip, brand-matched)
  const ogImage = `${process.env.REACT_APP_BACKEND_URL}/api/public/case-studies/og.png`;

  return (
    <div className="min-h-screen bg-canvas text-ink">
      <Helmet>
        <title>{seoTitle}</title>
        <meta name="description" content={seoDesc} />
        <link rel="canonical" href={`${PUBLIC_URL}/case-studies`} />
        <meta property="og:type" content="website" />
        <meta property="og:title" content={seoTitle} />
        <meta property="og:description" content={seoDesc} />
        <meta property="og:url" content={`${PUBLIC_URL}/case-studies`} />
        <meta property="og:image" content={ogImage} />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={seoTitle} />
        <meta name="twitter:description" content={seoDesc} />
        <meta name="twitter:image" content={ogImage} />
      </Helmet>

      <Nav />

      <main className="max-w-5xl mx-auto px-6 py-16 lg:py-24">
        <div className="mb-14">
          <div className="text-xs uppercase tracking-widest text-stone" data-testid="case-studies-tag">Case Study</div>
          <h1 className="font-serif text-4xl lg:text-5xl mt-3 text-ink leading-tight">
            Chi ha scelto <em className="italic text-terracotta">Otelya</em>, e cosa è successo dopo.
          </h1>
          <p className="mt-4 text-stone leading-relaxed max-w-2xl">
            Niente slide. Niente promesse. Dati reali: giorni-to-close, scambi AI, parole del gestore.
            Ogni case study viene generato automaticamente dal nostro ROI Tracker quando una struttura si abbona.
          </p>
        </div>

        {!items && <div className="text-stone">Caricamento…</div>}

        {items && items.length === 0 && (
          <div className="text-stone italic py-10 text-center border border-dashed border-line rounded-2xl" data-testid="case-studies-empty">
            Nessun case study pubblicato (ancora). I primi arriveranno quando il primo cliente ci dà il via libera alla pubblicazione.
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-testid="case-studies-list">
          {items?.map(cs => (
            <Link key={cs.id} to={`/case-study/${cs.slug}`} data-testid={`case-study-card-${cs.slug}`}
                  className="group block rounded-2xl border border-line bg-canvas hover:border-terracotta transition p-7">
              <div className="flex items-center justify-between">
                <div className="text-[10px] uppercase tracking-widest text-stone flex items-center gap-1">
                  <Buildings size={11}/> {cs.property_type || "struttura"}
                </div>
                <div className="text-[10px] uppercase tracking-widest text-gold">
                  {cs.plan_label || cs.plan_key}
                </div>
              </div>
              <h2 className="font-serif text-2xl mt-3 text-ink leading-snug group-hover:text-terracotta transition-colors">
                {cs.structure_name}
              </h2>
              {cs.city && (
                <div className="text-xs text-stone mt-1 flex items-center gap-1">
                  <MapPin size={12}/> {cs.city}
                </div>
              )}
              <div className="mt-5 grid grid-cols-3 gap-2 text-center">
                <div className="rounded-lg bg-sand/40 py-3">
                  <div className="font-serif text-xl text-ink">{daysLabel(cs.days_to_close)}</div>
                  <div className="text-[10px] uppercase tracking-widest text-stone mt-1">to close</div>
                </div>
                <div className="rounded-lg bg-sand/40 py-3">
                  <div className="font-serif text-xl text-ink">{cs.ai_exchanges || 0}</div>
                  <div className="text-[10px] uppercase tracking-widest text-stone mt-1">scambi AI</div>
                </div>
                <div className="rounded-lg bg-sand/40 py-3">
                  <div className="font-serif text-xl text-ink">{cs.clicks_before_close || 0}</div>
                  <div className="text-[10px] uppercase tracking-widest text-stone mt-1">click</div>
                </div>
              </div>
              <div className="mt-5 inline-flex items-center gap-1 text-sm text-ink font-medium">
                Leggi la storia <ArrowRight size={14} weight="bold"/>
              </div>
            </Link>
          ))}
        </div>

        {items && items.length > 0 && (
          <div className="mt-16 rounded-2xl border border-line bg-sand/30 p-8 text-center">
            <div className="font-serif text-2xl text-ink">Vuoi essere il prossimo case study?</div>
            <p className="mt-2 text-sm text-stone max-w-xl mx-auto">
              Attivi Otelya, lasci che la nostra AI parli con i tuoi ospiti per 7 giorni, e il tuo case study si scrive da solo.
            </p>
            <Link to="/#pricing">
              <Button className="mt-5 bg-ink text-canvas hover:bg-ink/90 rounded-full px-8" data-testid="case-studies-cta">
                Attiva Otelya ora
              </Button>
            </Link>
          </div>
        )}
      </main>

      <footer className="max-w-5xl mx-auto px-6 py-10 text-xs text-stone text-center border-t border-line">
        © {new Date().getFullYear()} Otelya — Case Study auto-generati dal ROI Tracker.
      </footer>
    </div>
  );
}

// ----------------------------- DETAIL -----------------------------

function ShareButtons({ cs, onShare }) {
  // Use the SSR-rendered /api/share/case-study/... URL so social crawlers see OG tags
  const shareUrl = `${PUBLIC_URL}/api/share/case-study/${cs.slug}`;
  const shareText = cs.testimonial
    ? `"${cs.testimonial.slice(0, 140)}${cs.testimonial.length > 140 ? '…' : ''}" — ${cs.structure_name}${cs.city ? ', ' + cs.city : ''} · case study Otelya`
    : `${cs.structure_name} ha chiuso con la receptionist AI di Otelya in ${cs.days_to_close !== null && cs.days_to_close !== undefined ? cs.days_to_close + ' giorni' : 'pochi giorni'}.`;
  const u = encodeURIComponent(shareUrl);
  const t = encodeURIComponent(shareText);
  const track = (type) => { try { onShare && onShare(type); } catch {} };

  const links = {
    linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${u}`,
    whatsapp: `https://wa.me/?text=${t}%20${u}`,
    x: `https://twitter.com/intent/tweet?url=${u}&text=${t}`,
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      track("share_copy");
      toast.success("Link copiato ✓");
    } catch {
      toast.error("Copia non supportata — seleziona manualmente");
    }
  };

  return (
    <section className="rounded-2xl border border-line bg-canvas p-6 mb-12" data-testid="case-study-share">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-2 text-sm text-stone">
          <ShareNetwork size={18} className="text-terracotta"/>
          <span>Trovato utile? <strong className="text-ink">Diffondilo in 1 click.</strong></span>
        </div>
        <div className="flex items-center gap-2">
          <a href={links.linkedin} target="_blank" rel="noopener noreferrer"
             aria-label="Condividi su LinkedIn"
             onClick={() => track("share_linkedin")}
             className="h-10 w-10 rounded-full border border-line flex items-center justify-center text-ink hover:bg-[#0A66C2] hover:text-canvas hover:border-[#0A66C2] transition-colors"
             data-testid="share-linkedin">
            <LinkedinLogo size={18} weight="fill"/>
          </a>
          <a href={links.whatsapp} target="_blank" rel="noopener noreferrer"
             aria-label="Condividi su WhatsApp"
             onClick={() => track("share_whatsapp")}
             className="h-10 w-10 rounded-full border border-line flex items-center justify-center text-ink hover:bg-[#25D366] hover:text-canvas hover:border-[#25D366] transition-colors"
             data-testid="share-whatsapp">
            <WhatsappLogo size={18} weight="fill"/>
          </a>
          <a href={links.x} target="_blank" rel="noopener noreferrer"
             aria-label="Condividi su X (Twitter)"
             onClick={() => track("share_x")}
             className="h-10 w-10 rounded-full border border-line flex items-center justify-center text-ink hover:bg-ink hover:text-canvas transition-colors"
             data-testid="share-x">
            <XLogo size={16} weight="fill"/>
          </a>
          <button onClick={copyLink}
                  aria-label="Copia link"
                  className="h-10 w-10 rounded-full border border-line flex items-center justify-center text-ink hover:bg-sand transition-colors"
                  data-testid="share-copy">
            <Copy size={16}/>
          </button>
        </div>
      </div>
    </section>
  );
}

export function CaseStudyDetail() {
  const { slug } = useParams();
  const [cs, setCs] = useState(null);
  const [notFound, setNotFound] = useState(false);

  // Analytics: session id in localStorage (24h dedup matches backend TTL)
  const getSessionId = () => {
    try {
      let sid = localStorage.getItem("otelya_cs_sid");
      const exp = Number(localStorage.getItem("otelya_cs_sid_exp") || 0);
      if (!sid || Date.now() > exp) {
        sid = `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
        localStorage.setItem("otelya_cs_sid", sid);
        localStorage.setItem("otelya_cs_sid_exp", String(Date.now() + 24 * 3600 * 1000));
      }
      return sid;
    } catch { return `anon-${Math.random().toString(36).slice(2)}`; }
  };
  const trackEvent = (type) => {
    try {
      api.post(`/public/case-study/${slug}/event`, { type, session_id: getSessionId() }).catch(() => {});
    } catch {}
  };

  useEffect(() => {
    api.get(`/public/case-study/${slug}`)
      .then(r => { setCs(r.data); trackEvent("view"); })
      .catch(() => setNotFound(true));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  if (notFound) {
    return (
      <div className="min-h-screen bg-canvas text-ink">
        <Nav/>
        <main className="max-w-3xl mx-auto px-6 py-24 text-center">
          <h1 className="font-serif text-3xl">Case study non trovato</h1>
          <p className="text-stone mt-3">Potrebbe essere ancora in bozza. <Link to="/case-studies" className="text-terracotta underline">Torna alla lista</Link>.</p>
        </main>
      </div>
    );
  }
  if (!cs) {
    return (
      <div className="min-h-screen bg-canvas text-ink">
        <Nav/>
        <main className="max-w-3xl mx-auto px-6 py-24 text-stone">Caricamento…</main>
      </div>
    );
  }

  const canonical = `${PUBLIC_URL}/case-study/${cs.slug}`;
  const seoTitle = `${cs.structure_name}${cs.city ? ' · ' + cs.city : ''} · Case Study Otelya`;
  const seoDesc = cs.testimonial
    ? `${cs.testimonial.slice(0, 160)}…`
    : `Come ${cs.structure_name} ha chiuso il contratto in ${daysLabel(cs.days_to_close)} dopo ${cs.ai_exchanges} scambi con la receptionist AI di Otelya.`;
  // Dynamic OG image generated by backend (1200×630, brand-matched)
  const ogImage = `${process.env.REACT_APP_BACKEND_URL}/api/public/case-study/${cs.slug}/og.png`;

  const ldJson = {
    "@context": "https://schema.org",
    "@type": "Article",
    "@id": canonical + "#article",
    "headline": seoTitle,
    "description": seoDesc,
    "datePublished": cs.published_at || cs.created_at,
    "dateModified": cs.updated_at || cs.created_at,
    "inLanguage": "it-IT",
    "url": canonical,
    "mainEntityOfPage": { "@type": "WebPage", "@id": canonical },
    "author": { "@type": "Organization", "name": "Otelya", "url": PUBLIC_URL + "/" },
    "publisher": {
      "@type": "Organization",
      "name": "Otelya",
      "logo": { "@type": "ImageObject", "url": PUBLIC_URL + "/otelya-icon-512.png" }
    },
    ...(cs.testimonial ? {
      "review": {
        "@type": "Review",
        "reviewBody": cs.testimonial,
        "author": { "@type": "Person", "name": cs.testimonial_author || "Cliente verificato" },
        "reviewRating": { "@type": "Rating", "ratingValue": "5", "bestRating": "5" }
      }
    } : {})
  };

  return (
    <div className="min-h-screen bg-canvas text-ink">
      <Helmet>
        <title>{seoTitle}</title>
        <meta name="description" content={seoDesc} />
        <link rel="canonical" href={canonical} />
        <meta property="og:type" content="article" />
        <meta property="og:title" content={seoTitle} />
        <meta property="og:description" content={seoDesc} />
        <meta property="og:url" content={canonical} />
        <meta property="og:image" content={ogImage} />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:locale" content="it_IT" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={seoTitle} />
        <meta name="twitter:description" content={seoDesc} />
        <meta name="twitter:image" content={ogImage} />
        <script type="application/ld+json">{JSON.stringify(ldJson).replace(/</g, "\\u003c")}</script>
      </Helmet>

      <Nav />

      <main className="max-w-3xl mx-auto px-6 py-16 lg:py-24">
        <Link to="/case-studies" className="inline-flex items-center gap-1 text-sm text-stone hover:text-ink mb-8" data-testid="case-study-back">
          <ArrowLeft size={14}/> Tutti i case study
        </Link>

        {/* HERO */}
        <div data-testid="case-study-hero" className="mb-10">
          <div className="text-xs uppercase tracking-widest text-terracotta flex items-center gap-2">
            <Buildings size={12}/> {cs.property_type || "struttura ricettiva"}
            {cs.city && <><span>·</span><MapPin size={12}/> {cs.city}</>}
          </div>
          <h1 className="font-serif text-4xl lg:text-5xl mt-4 text-ink leading-[1.1]" data-testid="case-study-structure-name">
            {cs.structure_name}
          </h1>
          <p className="mt-4 text-stone max-w-2xl leading-relaxed">
            Attivata Otelya il <strong>{new Date(cs.created_at).toLocaleDateString("it-IT", { day: "numeric", month: "long", year: "numeric" })}</strong>.
            Piano: <em>{cs.plan_label || cs.plan_key}</em>.
          </p>
        </div>

        {/* KPI */}
        <div className="grid grid-cols-3 gap-3 lg:gap-6 mb-12" data-testid="case-study-kpis">
          <div className="rounded-2xl border border-line bg-sand/30 p-5 text-center">
            <Clock size={22} className="mx-auto text-terracotta"/>
            <div className="font-serif text-3xl mt-2 text-ink">{daysLabel(cs.days_to_close)}</div>
            <div className="text-[10px] uppercase tracking-widest text-stone mt-1">dal primo contatto</div>
          </div>
          <div className="rounded-2xl border border-line bg-sand/30 p-5 text-center">
            <ChatCircleDots size={22} className="mx-auto text-terracotta"/>
            <div className="font-serif text-3xl mt-2 text-ink">{cs.ai_exchanges || 0}</div>
            <div className="text-[10px] uppercase tracking-widest text-stone mt-1">scambi AI ↔ cliente</div>
          </div>
          <div className="rounded-2xl border border-line bg-sand/30 p-5 text-center">
            <CheckCircle size={22} className="mx-auto text-terracotta"/>
            <div className="font-serif text-3xl mt-2 text-ink">0</div>
            <div className="text-[10px] uppercase tracking-widest text-stone mt-1">chiamate umane fatte</div>
          </div>
        </div>

        {/* INTRO (admin editable) */}
        {cs.intro_html && (
          <div className="prose max-w-none prose-headings:font-serif prose-headings:text-ink prose-p:text-stone prose-p:leading-relaxed mb-12"
               dangerouslySetInnerHTML={{ __html: cs.intro_html }}
               data-testid="case-study-intro"/>
        )}

        {/* FIRST EMAIL (anonymized) */}
        {cs.first_email_body_anon && (
          <section className="rounded-2xl border border-line bg-canvas p-7 mb-12" data-testid="case-study-first-email">
            <div className="text-xs uppercase tracking-widest text-stone mb-3">La prima email · anonimizzata</div>
            {cs.first_email_subject && (
              <div className="font-serif text-lg text-ink mb-3">{cs.first_email_subject}</div>
            )}
            <pre className="whitespace-pre-wrap font-sans text-sm text-stone leading-relaxed border-l-2 border-terracotta/60 pl-4">
{cs.first_email_body_anon}
            </pre>
            <div className="mt-4 text-[11px] text-stone italic">
              Email automatica inviata al gestore. Dati sensibili (nome, telefono, email, P.IVA) sono stati mascherati automaticamente.
            </div>
          </section>
        )}

        {/* TESTIMONIAL */}
        {cs.testimonial && (
          <section className="rounded-2xl bg-ink text-canvas p-8 mb-12 relative" data-testid="case-study-testimonial">
            <Quotes size={32} className="text-terracotta mb-4"/>
            <blockquote className="font-serif text-xl lg:text-2xl leading-snug italic">
              {cs.testimonial}
            </blockquote>
            <div className="mt-6 text-sm text-canvas/70">
              — <strong className="text-canvas">{cs.testimonial_author || "Cliente verificato"}</strong>
              {cs.testimonial_role && <span>, {cs.testimonial_role}</span>}
            </div>
          </section>
        )}

        {/* SHARE */}
        <ShareButtons cs={cs} onShare={trackEvent} />

        {/* CTA */}
        <section className="rounded-2xl border border-line bg-sand/30 p-8 text-center">
          <Confetti size={30} className="mx-auto text-terracotta"/>
          <div className="font-serif text-2xl mt-3 text-ink">Fai come {cs.structure_name}.</div>
          <p className="mt-2 text-sm text-stone max-w-xl mx-auto">
            Lascia che l'AI risponda ai tuoi ospiti H24. Il case study tuo si genera da solo.
          </p>
          <Link to="/#pricing" onClick={() => trackEvent("cta_click")}>
            <Button className="mt-5 bg-ink text-canvas hover:bg-ink/90 rounded-full px-8" data-testid="case-study-cta">
              Attiva Otelya ora →
            </Button>
          </Link>
        </section>
      </main>

      <footer className="max-w-3xl mx-auto px-6 py-10 text-xs text-stone text-center border-t border-line">
        © {new Date().getFullYear()} Otelya — Case Study verificato ROI Tracker
      </footer>
    </div>
  );
}

// ----------------------- TESTIMONIAL SUBMIT -----------------------

export function CaseStudyTestimonialSubmit() {
  const { slug } = useParams();
  const [sp] = useSearchParams();
  const token = sp.get("t") || "";
  const [content, setContent] = useState("");
  const [author, setAuthor] = useState("");
  const [role, setRole] = useState("");
  const [showName, setShowName] = useState(true);
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (content.trim().length < 20) {
      toast.error("Scrivi almeno 20 caratteri — poche righe sincere bastano.");
      return;
    }
    setLoading(true);
    try {
      await api.post(`/public/case-study/${slug}/testimonial`, {
        token, author, role, content, show_name: showName,
      });
      setSent(true);
      toast.success("Grazie! Testimonianza ricevuta.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore. Riprova.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-canvas text-ink">
      <Nav/>
      <main className="max-w-xl mx-auto px-6 py-16 lg:py-24">
        <div className="text-xs uppercase tracking-widest text-stone">Testimonianza</div>
        <h1 className="font-serif text-3xl lg:text-4xl mt-3 text-ink leading-tight">
          Due minuti. <em className="italic text-terracotta">Zero marketing.</em>
        </h1>
        <p className="mt-4 text-stone leading-relaxed">
          Raccontami brevemente com'è andata la tua esperienza con Otelya. Pubblicherò queste parole sulla pagina del tuo case study — se preferisci rimanere anonimo, puoi.
        </p>

        {sent ? (
          <div className="mt-10 rounded-2xl border border-olive bg-olive/10 p-8 text-center" data-testid="testimonial-sent">
            <CheckCircle size={32} className="mx-auto text-olive"/>
            <div className="font-serif text-2xl mt-3 text-ink">Grazie di cuore.</div>
            <p className="mt-2 text-sm text-stone">Rivedrò la tua testimonianza e la pubblicherò entro 24 ore.</p>
            <Link to="/" className="inline-block mt-6 text-sm text-terracotta underline">Torna a otelya.app</Link>
          </div>
        ) : (
          <form onSubmit={submit} className="mt-10 space-y-5" data-testid="testimonial-form">
            <div>
              <Label htmlFor="t-content">La tua testimonianza (20-1200 caratteri)</Label>
              <Textarea id="t-content" value={content} onChange={e=>setContent(e.target.value)}
                        placeholder="Es: Con Otelya ho smesso di perdere richieste nel weekend. In 10 giorni ho firmato 3 prenotazioni che prima scappavano…"
                        rows={6} data-testid="testimonial-content"/>
              <div className="text-[11px] text-stone mt-1">{content.length}/1200</div>
            </div>
            <div>
              <Label htmlFor="t-author">Il tuo nome (o nickname)</Label>
              <Input id="t-author" value={author} onChange={e=>setAuthor(e.target.value)}
                     placeholder="Es: Marco R." data-testid="testimonial-author"/>
            </div>
            <div>
              <Label htmlFor="t-role">Ruolo / struttura (opzionale)</Label>
              <Input id="t-role" value={role} onChange={e=>setRole(e.target.value)}
                     placeholder="Es: Owner, Agriturismo Le Querce" data-testid="testimonial-role"/>
            </div>
            <label className="flex items-center gap-2 text-sm text-stone">
              <input type="checkbox" checked={showName} onChange={e=>setShowName(e.target.checked)}
                     data-testid="testimonial-showname"/>
              Pubblica con il mio nome (se togli, apparirà "Cliente verificato")
            </label>
            <Button type="submit" disabled={loading}
                    className="bg-ink text-canvas hover:bg-ink/90 rounded-full px-8"
                    data-testid="testimonial-submit">
              {loading ? "Invio…" : (<><PaperPlaneTilt size={14} className="mr-2"/> Invia testimonianza</>)}
            </Button>
          </form>
        )}
      </main>
    </div>
  );
}
